import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { usePracticeArm } from "../../domains/practice/hooks/usePracticeArm";
import { useApiQuery } from "../../services/api";
import DiscoveryPage from "./DiscoveryPage";
import PracticeFeedPage from "../practice-feed/PracticeFeedPage";

/**
 * Which `/discovery` the reader gets.
 *
 * The whole flag lives here, and it decides between two WHOLE pages rather than
 * a dozen conditionals inside one: the arm that loses is deleted as a file.
 *
 * TWO conditions, not one. Being in the arm is not enough — the reader must also
 * HAVE a practice, because the practice page's chips, its union feed and its
 * whole navigation are that catalogue. A reader in the arm holding nothing would
 * get a feed surface with nothing to feed it, on the app's main tab. The arm is
 * how the feature is rolled out; a non-empty catalogue is what makes the page
 * mean anything.
 *
 * The catalogue read is FREE: `PracticeFeedPage` already issues the exact
 * same query, so react-query serves both from one key and one request.
 *
 * An unsettled arm renders TODAY'S page, not a loader, and that is the
 * load-bearing decision here. `pending` is not bounded: if
 * `growthbook.setAttributes()` rejects, `growthbookIdentityStore` publishes a
 * failure (`App.tsx`) and the arm stays pending for the whole session. The
 * rail could answer that with "no rail"; this is the app's main tab, and a
 * spinner that can never resolve is a dead tab on a GrowthBook outage. Today —
 * before this lot — `/discovery` renders unconditionally, and that is the
 * property being preserved. The catalogue read inherits the same rule: a request
 * in flight, or one that failed, renders today's page rather than waiting.
 *
 * The cost is a practice-arm reader seeing the ordinary surface for the frames
 * before the payload lands, then the swap — and that window is now a network
 * round trip rather than a CDN payload, so it is longer. Deliberately not paid in
 * measurement: an impression needs ≥1s in the viewport
 * (`useViewportImpression`), which the settled state beats. What it does cost is
 * one wasted `/webapp/private/feed` request, and the risk that a reader tapping
 * within those frames has the surface change under their thumb
 * (`.knowledge/growthbook-oneshot-flag-race.md`). Both beat a tab that hangs.
 *
 * A signed-out reader is served the ordinary page immediately and never waits:
 * the arm is `pending` forever without a user to evaluate, and that page is the
 * one with the offline path. Nobody is bucketed anonymously.
 */
function DiscoveryArm(): React.JSX.Element {
  const { currentUser } = useCurrentUser();
  const arm = usePracticeArm();
  const catalogue = useApiQuery(
    "/webapp/private/practice-profiles",
    arm === "in" ? {} : false
  );

  if (currentUser === null) return <DiscoveryPage />;
  if (arm !== "in") return <DiscoveryPage />;
  if (catalogue.state !== "success") return <DiscoveryPage />;
  if (catalogue.body.docs.length === 0) return <DiscoveryPage />;

  return <PracticeFeedPage />;
}

export default function DiscoverySurface(): React.JSX.Element {
  return <DiscoveryArm />;
}
